import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
    Dialog, DialogContent, DialogDescription,
    DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { LucideIcon, useFile, message } from "../ui/ot";

/**
 * UploadFile
 *
 * Props:
 *   element      - 元件設定物件（id, multiple, placeholder, showFileList, canView, required）
 *   label        - 欄位標題文字
 *   disabled     - 是否禁用
 *   defaultValue - 預設 file_id（數字 / 字串 / 陣列），傳入即自動向 API 取回並顯示
 *   onConfirm    - 上傳或移除後回呼；single → file_id, multiple → file_id[]
 *   apiRoute     - 上傳 API，預設 '/component/file'
 *   getApi       - 取檔 API，預設 '/utility/file/blob'
 */
const UploadFile = ({
    element = {},
    label = "",
    disabled = false,
    defaultValue,
    onConfirm = () => { },
    apiRoute = "/component/file",
    getApi = "/utility/file/blob",
    fetchHeaders = {},   // ← 若專案需要帶 token，從外部傳入 e.g. { Authorization: `Bearer ${token}` }
}) => {
    const isMultiple = !!element?.multiple;
    const inputId = `${element?.id ?? "upload"}-file-input`;

    /**
     * 每筆檔案結構：
     *   { _key, file_id, name, type, size, url, contentText, _pending }
     *   - _key     : 唯一識別（用來精準更新，避免多檔同時 pending 互蓋）
     *   - _pending : true 表示還在上傳中，尚未取得 file_id
     *   - url=null : 有 file_id 但還沒向 API 取回（交給 FileItem 處理）
     */
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const keyCounter = useRef(0);
    const nextKey = () => `k-${++keyCounter.current}`;

    // ── 檔案預覽 Dialog ──────────────────────────────────────────────
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [viewingFile, setViewingFile] = useState(null);

    // ── defaultValue 初始化 ──────────────────────────────────────────
    // 監聽 defaultValue 變動（包含首次 mount）
    // element.content 可能是數字 4775、字串 "4775"，或陣列
    useEffect(() => {
        if (defaultValue == null || defaultValue === "" || defaultValue === 0) {
            setUploadedFiles([]);
            return;
        }
        const ids = (Array.isArray(defaultValue) ? defaultValue : [defaultValue])
            .map(Number)
            .filter(Boolean); // 過濾 0 / NaN

        if (ids.length === 0) {
            setUploadedFiles([]);
            return;
        }

        // 建立只含 file_id 的 placeholder，讓 <FileItem> 去 fetch 詳情
        setUploadedFiles(
            ids.map((id) => ({
                _key: nextKey(),
                file_id: id,
                name: "",
                type: "",
                size: 0,
                url: null,       // null → FileItem 負責取回
                _pending: false,
            }))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    // ── 上傳 hook（用 _key 精準更新對應筆） ─────────────────────────
    const { postFileData } = useFile({
        apiRoute,
        onFinish: ({ axiosMode, status, values, extra }) => {
            if (status !== "success" || axiosMode !== "post") return;
            message.success("檔案上傳成功");
            const fileId = Number(values?.file_id?.file_id ?? values?.file_id);
            const pendingKey = extra?._key;

            setUploadedFiles((prev) => {
                const updated = prev.map((f) =>
                    f._key === pendingKey
                        ? { ...f, file_id: fileId, _pending: true }
                        : { ...f, file_id: fileId, _pending: true }
                );
                fireConfirm(updated);
                return updated;
            });
        },
    });

    const fireConfirm = (files) => {
        const ids = files.map((f) => f.file_id).filter(Boolean);
        onConfirm(isMultiple ? ids : ids[0] ?? null);
    };

    // ── 讀取本地檔案建立 objectURL ───────────────────────────────────
    const readLocalFile = (file) =>
        new Promise((resolve) => {
            const url = URL.createObjectURL(file);
            const type = file.type || "";
            if (type.startsWith("text/") || type === "application/json") {
                const reader = new FileReader();
                reader.onload = () => resolve({ url, contentText: reader.result });
                reader.readAsText(file);
            } else {
                resolve({ url });
            }
        });

    // ── 選擇檔案 ─────────────────────────────────────────────────────
    const handleFileChange = async (e) => {
        if (disabled) return;
        const fileList = Array.from(e.target.files || []);
        if (!fileList.length) return;
        e.target.value = "";

        const newEntries = await Promise.all(
            fileList.map(async (file) => {
                const { url, contentText } = await readLocalFile(file);
                return {
                    _key: nextKey(),
                    _pending: true,
                    _file: file,
                    file_id: null,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    url,
                    contentText,
                };
            })
        );

        setUploadedFiles((prev) => {
            return isMultiple ? [...prev, ...newEntries] : newEntries.slice(0, 1);
        });

        // 各自上傳，帶 _key 讓 onFinish 可精準對應
        newEntries.forEach((entry) => {
            postFileData({ data: entry._file, extra: { _key: entry._key } });
        });
    };

    // ── 移除 ─────────────────────────────────────────────────────────
    const handleRemove = (key) => {
        setUploadedFiles((prev) => {
            const next = prev.filter((f) => f._key !== key);
            fireConfirm(next);
            return next;
        });
    };

    // ── FileItem 取回詳情後，更新該筆的 name/type/size/url ───────────
    const handleItemResolved = (key, info) => {
        setUploadedFiles((prev) =>
            prev.map((f) => (f._key === key ? { ...f, ...info } : f))
        );
    };

    return (
        <div className="space-y-2 w-full pointer-events-auto">
            {/* ── 預覽 Dialog ── */}
            <Dialog
                open={fileViewerOpen}
                onOpenChange={(open) => {
                    setFileViewerOpen(open);
                    if (!open) setViewingFile(null);
                }}
            >
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="truncate">
                            {viewingFile?.name || "檔案預覽"}
                        </DialogTitle>
                        <DialogDescription className="truncate">
                            {viewingFile?.type || ""}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2 max-h-[70vh] overflow-auto">
                        {viewingFile && (
                            viewingFile.type?.startsWith("image/") ? (
                                <img
                                    src={viewingFile.url}
                                    alt={viewingFile.name}
                                    className="max-w-full h-auto mx-auto"
                                />
                            ) : viewingFile.type === "application/pdf" ? (
                                <iframe
                                    src={viewingFile.url}
                                    title={viewingFile.name}
                                    className="w-full h-[70vh] border"
                                />
                            ) : viewingFile.type?.startsWith("text/") || viewingFile.type === "application/json" ? (
                                <pre className="whitespace-pre-wrap text-sm bg-muted/30 p-3 rounded border">
                                    {typeof viewingFile.contentText === "string"
                                        ? viewingFile.contentText
                                        : "無法以文字模式預覽"}
                                </pre>
                            ) : (
                                <div className="text-sm text-muted-foreground">
                                    此檔案類型暫不支援預覽，您可以下載檔案以查看內容。
                                </div>
                            )
                        )}
                    </div>
                    <DialogFooter className="flex items-center justify-between">
                        <Button type="button" variant="outline" onClick={() => setFileViewerOpen(false)}>
                            關閉
                        </Button>
                        {viewingFile?.url && (
                            <a href={viewingFile.url} download={viewingFile.name} className="inline-flex items-center">
                                <Button type="button" variant="default">下載檔案</Button>
                            </a>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ── 標題 ── */}
            <Label>
                {label || "附件上傳"}
                {element?.required && <span className="text-red-500 ml-1">*</span>}
            </Label>

            {/* ── 上傳區域 ── */}
            <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => { if (!disabled) document.getElementById(inputId)?.click(); }}
                aria-disabled={disabled}
            >
                <LucideIcon name="Upload" className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                    {element?.placeholder || "點擊上傳檔案"}
                </p>
                <input
                    id={inputId}
                    type="file"
                    className="hidden"
                    multiple={isMultiple}
                    disabled={disabled}
                    onChange={handleFileChange}
                />
            </div>

            {/* ── 檔案清單 ── */}
            {element?.showFileList !== false && uploadedFiles.length > 0 && (
                <div className="mt-2 border rounded-md bg-white divide-y">
                    {uploadedFiles.map((f) =>
                        // 有 file_id 但還沒 url → 交給 FileItem 向 API 取回
                        f.file_id && !f.url ? (
                            <FileItem
                                key={f._key}
                                fileKey={f._key}
                                file_id={f.file_id}
                                getApi={getApi}
                                fetchHeaders={fetchHeaders}
                                canView={!!element?.canView}
                                disabled={disabled}
                                onResolved={(info) => handleItemResolved(f._key, info)}
                                onView={(info) => { setViewingFile(info); setFileViewerOpen(true); }}
                                onRemove={() => handleRemove(f._key)}
                            />
                        ) : (
                            <FileRow
                                key={f._key}
                                file={f}
                                canView={!!element?.canView}
                                disabled={disabled}
                                onView={() => { setViewingFile(f); setFileViewerOpen(true); }}
                                onRemove={() => handleRemove(f._key)}
                            />
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default UploadFile;


// ─────────────────────────────────────────────────────────────────────────────
// 從副檔名推斷 MIME type
// PHP 固定回傳 application/octet-stream，必須從檔名自行判斷
// ─────────────────────────────────────────────────────────────────────────────
const EXT_MIME = {
    // 圖片
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
    gif: "image/gif", webp: "image/webp", svg: "image/svg+xml",
    bmp: "image/bmp", ico: "image/x-icon", tiff: "image/tiff", tif: "image/tiff",
    // 文件
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    // 文字
    txt: "text/plain", csv: "text/csv", json: "application/json",
    xml: "text/xml", html: "text/html", htm: "text/html",
    // 壓縮
    zip: "application/zip", rar: "application/x-rar-compressed",
    "7z": "application/x-7z-compressed", tar: "application/x-tar",
    gz: "application/gzip",
    // 影音
    mp4: "video/mp4", mp3: "audio/mpeg", wav: "audio/wav",
    mov: "video/quicktime", avi: "video/x-msvideo",
};

const getMimeFromFilename = (filename = "") => {
    const ext = filename.split(".").pop()?.toLowerCase() ?? "";
    return EXT_MIME[ext] || "application/octet-stream";
};
//
// PHP 直接把 file_client_name（中文字串）塞進 header，沒有做 RFC 5987 encode：
//   Content-Disposition: attachment;filename="承租人資料表.pdf"
//
// 瀏覽器在解析 HTTP header 時預設把所有 header 值當作 ISO-8859-1 (latin1)，
// 所以中文字會被誤讀成亂碼。需要先把 latin1 字串轉回原始 bytes，
// 再用 TextDecoder 以 UTF-8 重新解讀。
// ─────────────────────────────────────────────────────────────────────────────
const decodeFilename = (raw = "") => {
    try {
        const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
        return new TextDecoder("utf-8").decode(bytes);
    } catch {
        return raw;
    }
};

const parseFilenameFromDisposition = (disposition = "") => {
    if (!disposition) return null;

    // 1. RFC 5987：filename*=UTF-8''encoded（PHP 若有做 encode 走這條）
    const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;\s]+)/i);
    if (utf8Match) {
        try { return decodeURIComponent(utf8Match[1]); } catch { /* ignore */ }
    }

    // 2. 一般 filename="..." 或 filename=...（PHP 實際走這條，中文需要 latin1→utf8）
    const match = disposition.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;\s]+)/i);
    if (match) {
        const raw = (match[1] ?? match[2] ?? "").trim();
        return decodeFilename(raw);
    }

    return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// FileItem：只有 file_id → 用原生 fetch 取回 binary
// 使用 fetch 而非 useFile，目的是能讀取 Content-Disposition header 取得原始檔名
//
// fetchHeaders prop：若專案有 Authorization / X-Token 等需求，從外部傳入
//   e.g. fetchHeaders={{ Authorization: `Bearer ${token}` }}
// ─────────────────────────────────────────────────────────────────────────────
const FileItem = ({ fileKey, file_id, getApi, canView, disabled, onResolved, onView, onRemove, fetchHeaders = {} }) => {
    const [resolvedFile, setResolvedFile] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!file_id) return;
        let objectUrl = null;
        let cancelled = false;

        const fetchFile = async () => {
            try {
                const res = await fetch(`${getApi}?file_id=${file_id}`, {
                    credentials: "include",   // 帶 cookie / session
                    headers: fetchHeaders,    // 帶入 token 等自訂 header
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                // ── 從 header 取原始檔名（含 latin1→utf8 修正） ──────
                const disposition = res.headers.get("Content-Disposition") || "";
                const clientName = parseFilenameFromDisposition(disposition);

                const name = clientName || `附件_${file_id}`;

                // ── PHP 固定回傳 application/octet-stream，從檔名副檔名判斷真實 MIME ──
                const mimeFromName = getMimeFromFilename(name);
                // header 的 Content-Type 若已是具體類型（非 octet-stream）則優先用它
                const rawContentType = res.headers.get("Content-Type") || "";
                const contentType = (rawContentType && rawContentType !== "application/octet-stream")
                    ? rawContentType
                    : mimeFromName;

                // 用正確 MIME 建立 Blob，讓 objectURL 帶正確類型（影響瀏覽器預覽行為）
                const rawBlob = await res.blob();
                if (cancelled) return;
                const blob = new Blob([rawBlob], { type: contentType });

                objectUrl = URL.createObjectURL(blob);

                const info = {
                    file_id,
                    name,
                    type: contentType,
                    size: blob.size,
                    url: objectUrl,
                };
                setResolvedFile(info);
                onResolved?.(info);
            } catch (err) {
                console.error("[FileItem] fetch failed:", err);
                if (!cancelled) setError(true);
            }
        };

        fetchFile();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file_id]);

    if (error) {
        return (
            <div className="flex items-center justify-between px-3 py-2 text-sm">
                <div className="flex items-center gap-2 text-destructive">
                    <LucideIcon name="AlertCircle" className="w-4 h-4" />
                    載入失敗 (id: {file_id})
                </div>
                {!disabled && (
                    <Button type="button" size="sm" variant="ghost"
                        className="h-7 text-destructive hover:text-destructive"
                        onClick={onRemove} title="移除">
                        <LucideIcon name="X" className="w-3.5 h-3.5" />
                    </Button>
                )}
            </div>
        );
    }

    if (!resolvedFile) {
        return (
            <div className="flex items-center px-3 py-2 text-sm text-muted-foreground gap-2">
                <LucideIcon name="Loader2" className="w-4 h-4 animate-spin" />
                載入中… (id: {file_id})
            </div>
        );
    }

    return (
        <FileRow
            file={resolvedFile}
            canView={canView}
            disabled={disabled}
            onView={() => onView?.(resolvedFile)}
            onRemove={onRemove}
        />
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// FileRow：單筆檔案顯示列
// ─────────────────────────────────────────────────────────────────────────────
const FileRow = ({ file, canView, disabled, onView, onRemove }) => (
    <div className="flex items-center justify-between px-3 py-2 text-sm">
        <div className="min-w-0 flex-1 mr-2 truncate">
            <div className="font-medium truncate flex items-center gap-1">
                {file._pending && (
                    <LucideIcon name="Loader2" className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                )}
                {file.name || `附件_${file.file_id}`}
            </div>
            <div className="text-xs text-muted-foreground truncate">
                {file.type || "未知類型"}
                {file.size ? ` • ${(file.size / 1024).toFixed(1)} KB` : ""}
                {file.file_id ? ` • id: ${file.file_id}` : ""}
            </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
            {canView && file.url && (
                <Button
                    type="button" size="sm" variant="outline" className="h-7"
                    disabled={disabled}
                    onClick={onView}
                    title="檢視"
                >
                    檢視
                </Button>
            )}
            {!disabled && (
                <Button
                    type="button" size="sm" variant="ghost"
                    className="h-7 text-destructive hover:text-destructive"
                    onClick={onRemove}
                    title="移除"
                >
                    <LucideIcon name="X" className="w-3.5 h-3.5" />
                </Button>
            )}
        </div>
    </div>
);